import { useMemo, useState } from 'react';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import isWeekend from 'date-fns/isWeekend';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';

import { DatePicker } from './DatePicker';
import { TimePicker } from './TimePicker';

import type { FC } from 'react';

type HourTenthType = 0 | 1 | 2;
type MinuteTenthType = HourTenthType | 3 | 4 | 5;

type DigitType = HourTenthType | MinuteTenthType | 6 | 7 | 8 | 9;

export type TimeString = `${HourTenthType}${DigitType}:${MinuteTenthType}${DigitType}`;

export type DateTimePickerProps = {
  startDate: Date;
  endDate: Date;
  noWeekend?: boolean;
  times: string[];
  onChange: (date: Date | null) => void;
};

/**
 * DateTimePicker
 *
 * @property {number} [interval]
 */
export const DateTimePicker: FC<DateTimePickerProps> = ({
  startDate,
  endDate,
  times = [],
  onChange,
  noWeekend = true,
}) => {
  const [dateIndex, setDateIndex] = useState<number>(0); // TODO: limit the values to the indices of dates array
  const [timeIndex, setTimeIndex] = useState<number>(-1);
  const dates = useMemo(() => {
    return eachDayOfInterval({
      start: startDate,
      end: endDate,
    }).filter((date) => (noWeekend ? !isWeekend(date) : date));
  }, [startDate, endDate, noWeekend]);

  const onDateChange = (direction: number): void => {
    setDateIndex((index) => index + direction);
    setTimeIndex(-1);
    onChange(null);
  };

  const onTimeChange = (newIndex: number): void => {
    setTimeIndex(newIndex);
    const selectedTime = times[newIndex];
    if (!selectedTime) {
      return onChange(null);
    }
    const [selectedHour, selectedMinutes] = selectedTime.split(':');
    const selectedDate = dates[dateIndex] as Date;
    const dateTime = setMinutes(
      setHours(selectedDate, Number(selectedHour)),
      Number(selectedMinutes),
    );
    onChange(dateTime);
  };

  return (
    <>
      <DatePicker dates={dates} selectedIndex={dateIndex} onDateChange={onDateChange} />
      <TimePicker times={times} selectedIndex={timeIndex} onTimeChange={onTimeChange} />
    </>
  );
};
